import React, { useContext, useEffect, useMemo, useCallback, useState, useRef } from "react";
import { themeContext } from "../../context/theme";
import { gameContext } from "../../context/game";
import { CardOne } from "../cards/cardOne";
import { useGamepad } from "../../hooks/useGamepad";
import { playSound, focusOnMouseMove } from "../../utilities/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import styles from "./ConfirmationModal.module.css";

export const ConfirmationModal = ({
  isVisible,
  title,
  message,
  confirmText = "Yes",
  cancelText = "No",
  onConfirm,
  onCancel,
  confirmButtonStyle = "danger", // "danger" or "primary"
}) => {
  const { theme } = useContext(themeContext);
  const game = useContext(gameContext);
  const themeNumber = theme.replace("theme-", "");

  // Focusable elements for navigation
  const [gamepadEnabled, setGamepadEnabled] = useState(false);
  const modalRef = useRef(null);

  const focusableElements = useMemo(() => {
    return { elements: null, index: 0 };
  }, []);

  // Enable gamepad with delay to prevent immediate button press from opening/closing modal
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setGamepadEnabled(true);
      }, 200); // Small delay to prevent button that opened modal from closing it
      return () => clearTimeout(timer);
    } else {
      setGamepadEnabled(false);
    }
  }, [isVisible]);

  // Initialize focusable elements and auto-focus modal when opened
  useEffect(() => {
    if (isVisible && modalRef.current) {
      // Focus the modal container so keyboard events work
      modalRef.current.focus();

      if (gamepadEnabled) {
        const elements = modalRef.current.querySelectorAll(`.${styles.modalButton}`);
        focusableElements.elements = elements;
        focusableElements.index = 0;
      }
    }
  }, [isVisible, gamepadEnabled, focusableElements]);

  // Handle gamepad navigation
  const handleGamepadNavigation = useCallback((direction) => {
    if (!modalRef.current) return;

    const elements = modalRef.current.querySelectorAll(`.${styles.modalButton}`);
    if (elements.length === 0) return;

    focusableElements.elements = elements;

    if (focusableElements.index === undefined) {
      focusableElements.index = 0;
    }

    if (direction === 'left' || direction === 'up') {
      if (focusableElements.index <= 0) {
        focusableElements.index = elements.length - 1;
      } else {
        focusableElements.index--;
      }
    } else if (direction === 'right' || direction === 'down') {
      if (focusableElements.index >= elements.length - 1) {
        focusableElements.index = 0;
      } else {
        focusableElements.index++;
      }
    }

    if (elements[focusableElements.index]) {
      playSound(game, "navigation");
      elements[focusableElements.index].focus();
    }
  }, [focusableElements, game]);

  // Handle gamepad button presses
  const handleGamepadButtonPress = useCallback((button) => {
    if (button === 'X') {
      if (!modalRef.current) return;

      const elements = modalRef.current.querySelectorAll(`.${styles.modalButton}`);
      if (elements[focusableElements.index]) {
        playSound(game, "click");
        elements[focusableElements.index].click();
      }
    } else if (button === 'B') {
      // Close modal with B button
      playSound(game, "click");
      onCancel();
    }
  }, [focusableElements, onCancel, game]);

  // Use the useGamepad hook
  useGamepad({
    onNavigate: handleGamepadNavigation,
    onButtonPress: handleGamepadButtonPress,
    navigationDelay: 150,
    buttonDelay: 150,
    enabled: gamepadEnabled,
    gamepadIndex: null
  });

  if (!isVisible) return null;

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => {
        // Close modal if clicking on overlay (outside the modal box)
        if (e.target === e.currentTarget) {
          playSound(game, "click");
          onCancel();
        }
      }}
    >
      <div
        ref={modalRef}
        className={`${styles.modal} ${styles[`modal-theme-${themeNumber}`]}`}
        onKeyDown={(event) => {
              const modal = event.currentTarget;
              const elements = modal.querySelectorAll(`.${styles.modalButton}`);
              if (elements.length === 0) return;

              focusableElements.elements = elements;

              if (focusableElements.index === undefined) {
                focusableElements.index = 0;
              }

              if (event.key === 'ArrowUp') {
                event.preventDefault();
                if (focusableElements.index <= 0) {
                  focusableElements.index = elements.length - 1;
                } else {
                  focusableElements.index--;
                }
                if (elements[focusableElements.index]) {
                  playSound(game, "navigation");
                  elements[focusableElements.index].focus();
                }
              } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                if (focusableElements.index >= elements.length - 1) {
                  focusableElements.index = 0;
                } else {
                  focusableElements.index++;
                }
                if (elements[focusableElements.index]) {
                  playSound(game, "navigation");
                  elements[focusableElements.index].focus();
                }
              } else if (event.key === 'Enter') {
                event.preventDefault();
                if (elements[focusableElements.index]) {
                  playSound(game, "click");
                  elements[focusableElements.index].click();
                }
              } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation(); // Prevent event from bubbling to parent
                playSound(game, "click");
                onCancel();
              }
        }}
        onMouseMove={(event) => {
              const modal = event.currentTarget;
              const elements = modal.querySelectorAll(`.${styles.modalButton}`);
              if (elements.length > 0) {
                focusableElements.elements = elements;
                focusOnMouseMove(event, focusableElements, game, playSound);
          }
        }}
        tabIndex={0}
      >
        {/* Close button */}
        <button
          className={`${styles.closeButton} ${
            styles[`closeButton-theme-${themeNumber}`]
          }`}
          onClick={() => {
            playSound(game, "click");
            onCancel();
          }}
          tabIndex="-1"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className={styles.modalContent}>
          {/* Message */}
          <div className={styles.messageSection}>
            <p className={styles[`modalMessage-theme-${theme}`]}>
              {message}
            </p>
          </div>

          {/* Action Buttons */}
          <div className={styles.actionsSection}>
            <button
              onClick={() => {
                playSound(game, "click");
                onConfirm();
              }}
              className={`${styles.modalButton} ${
                confirmButtonStyle === "danger"
                  ? styles.dangerButton
                  : styles.confirmButton
              } ${
                styles[`${confirmButtonStyle}Button-theme-${themeNumber}`]
              }`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};